import type { TcpChannel } from '@casa/bridge';
import { utf8Decode, utf8Encode } from '@casa/util';
import { RtspParser, header, type RtspTextMessage } from './rtsp_parser.ts';
import { parseRtspUrl, resolveControlUrl } from './rtsp_url.ts';
import {
    findH264Track,
    parseSdp,
    sdpAttribute,
    videoCodecs,
    type H264Track,
    type SessionDescription,
} from './sdp.ts';

export class RtspError extends Error {
    override name = 'RtspError';
    /** Status code of the response, when the server answered. */
    readonly status: number | undefined;

    constructor(message: string, status?: number, options?: ErrorOptions) {
        super(message, options);
        this.status = status;
    }
}

export interface RtspClientOptions {
    userAgent?: string;
    /** How long a request may stay unanswered. Default 10 s. */
    requestTimeoutMs?: number;
    log?: (message: string) => void;
}

export interface RtspDescription {
    sdpText: string;
    sdp: SessionDescription;
    /** Base for the control URLs: Content-Base, Content-Location or the request URL. */
    baseUrl: string;
}

interface PendingRequest {
    resolve(response: RtspTextMessage) : void;
    reject(error: Error) : void;
    timer: ReturnType<typeof setTimeout>;
}

const DEFAULT_SESSION_TIMEOUT_S = 60;

/**
 * RTSP 1.0 client (RFC 2326) for one video track, with RTP interleaved on the RTSP connection.
 * Spike state: H.264 only, Basic authentication from the URL only (Digest, computed here from the
 * URL's credentials, when a camera needs it; decision §12), no RTCP receiver reports.
 */
export class RtspClient {
    readonly #channel: TcpChannel;
    readonly #url: string;
    readonly #options: RtspClientOptions;
    readonly #credentials: { username: string; password: string } | undefined;
    readonly #parser = new RtspParser();
    readonly #pending = new Map<number, PendingRequest>();
    readonly #interleavedListeners = new Set<(channel: number, payload: Uint8Array) => void>();
    readonly #closeListeners = new Set<(reason?: string) => void>();
    #cseq = 0;
    #authorization: string | undefined;
    #session: string | undefined;
    #sessionTimeoutS = DEFAULT_SESSION_TIMEOUT_S;
    #aggregateUrl: string;
    #keepAliveMethod = 'OPTIONS';
    #keepAliveTimer: ReturnType<typeof setInterval> | undefined;
    #closed = false;
    #rtpChannelId = 0;

    /** `channel` is an open connection to the host and port of `url` (see `parseRtspUrl`). */
    constructor(channel: TcpChannel, url: string, options: RtspClientOptions = {}) {
        const parsed = parseRtspUrl(url);
        this.#channel = channel;
        this.#url = parsed.requestUrl;
        this.#aggregateUrl = parsed.requestUrl;
        this.#options = options;
        if (parsed.username !== undefined) {
            this.#credentials = { username: parsed.username, password: parsed.password ?? '' };
        }
        channel.onData((chunk) => this.#receive(chunk));
        channel.onClose((reason) => this.#finish(reason ?? 'connection closed by the camera'));
    }

    /** Interleaved channel that carries the RTP packets of the track set up by `start`. */
    get rtpChannel() : number {
        return this.#rtpChannelId;
    }

    /** Every interleaved frame, RTP and RTCP. */
    onInterleaved(listener: (channel: number, payload: Uint8Array) => void) : () => void {
        this.#interleavedListeners.add(listener);

        return () => this.#interleavedListeners.delete(listener);
    }

    /** The RTP packets of the video track. */
    onRtp(listener: (packet: Uint8Array) => void) : () => void {
        return this.onInterleaved((channel, payload) => {
            if (channel === this.#rtpChannelId) {
                listener(payload);
            }
        });
    }

    /** Called once, when the connection is gone for any reason other than `stop`. */
    onClose(listener: (reason?: string) => void) : () => void {
        this.#closeListeners.add(listener);

        return () => this.#closeListeners.delete(listener);
    }

    /** OPTIONS, DESCRIBE, SETUP and PLAY for the first H.264 video track. */
    async start() : Promise<{ track: H264Track; description: RtspDescription }> {
        await this.optionsRequest();
        const description = await this.describe();
        const track = findH264Track(description.sdp);
        if (!track) {
            const codecs = videoCodecs(description.sdp).join(', ') || 'none';
            throw new RtspError(`no H.264 video track in the stream (video codecs offered: ${codecs})`);
        }
        await this.setup(resolveControlUrl(description.baseUrl, track.control));
        await this.play();

        return { track, description };
    }

    async optionsRequest() : Promise<string[]> {
        const response = await this.#request('OPTIONS', this.#url);
        const methods = (header(response, 'public') ?? '').split(',').map((method) => method.trim().toUpperCase());
        if (methods.includes('GET_PARAMETER')) {
            this.#keepAliveMethod = 'GET_PARAMETER';
        }
        return methods;
    }

    async describe() : Promise<RtspDescription> {
        const response = await this.#request('DESCRIBE', this.#url, { Accept: 'application/sdp' });
        const sdpText = utf8Decode(response.body);
        const sdp = parseSdp(sdpText);
        const baseUrl = header(response, 'content-base') ?? header(response, 'content-location') ?? this.#url;
        this.#aggregateUrl = resolveControlUrl(baseUrl, sdpAttribute(sdp.attributes, 'control'));

        return { sdpText, sdp, baseUrl };
    }

    async setup(trackUrl: string) : Promise<void> {
        const response = await this.#request('SETUP', trackUrl, {
            Transport: 'RTP/AVP/TCP;unicast;interleaved=0-1',
        });
        // "id;timeout=60"
        const [id = '', ...parameters] = (header(response, 'session') ?? '').split(';');
        if (id.trim() === '') {
            throw new RtspError('SETUP answer has no Session header');
        }

        this.#session = id.trim();
        for (const parameter of parameters) {
            const match = /^\s*timeout\s*=\s*(\d+)/i.exec(parameter);
            if (match) {
                this.#sessionTimeoutS = Number(match[1]) || DEFAULT_SESSION_TIMEOUT_S;
            }
        }
        // The server may pick other channel numbers than the ones asked for.
        const transport = header(response, 'transport') ?? '';
        if (!/RTP\/AVP\/TCP/i.test(transport)) {
            throw new RtspError(`the camera did not accept interleaved TCP transport (${transport || 'no Transport header'})`);
        }

        const interleaved = /interleaved\s*=\s*(\d+)/i.exec(transport);
        this.#rtpChannelId = interleaved ? Number(interleaved[1]) : 0;
    }

    async play() : Promise<void> {
        await this.#request('PLAY', this.#aggregateUrl);

        // Half the session timeout, so that one lost keep-alive does not end the session.
        const intervalMs = Math.max(1, this.#sessionTimeoutS / 2) * 1000;
        this.#keepAliveTimer = setInterval(() => void this.#keepAlive(), intervalMs);
    }

    /** TEARDOWN, then closes the connection. Close listeners are not called. */
    async stop() : Promise<void> {
        if (this.#closed) {
            return;
        }

        this.#closeListeners.clear();
        if (this.#session !== undefined) {
            try {
                await this.#request('TEARDOWN', this.#aggregateUrl, {}, 2000);
            } catch {
                // The camera may already be gone; the connection is closed either way.
            }
        }
        this.#finish('stopped');
        await this.#closeChannel();
    }

    #log(message: string) : void {
        this.#options.log?.(message);
    }

    async #keepAlive() : Promise<void> {
        try {
            await this.#request(this.#keepAliveMethod, this.#aggregateUrl);
        } catch (error) {
            this.#log(`rtsp: keep-alive failed (${String(error)})`);
        }
    }

    async #closeChannel() : Promise<void> {
        try {
            await this.#channel.close();
        } catch {
            // Closing a connection that is already closed is not a problem.
        }
    }

    #receive(chunk: Uint8Array) : void {
        if (this.#closed) {
            return;
        }

        let messages;
        try {
            messages = this.#parser.push(chunk);
        } catch (error) {
            this.#finish(error instanceof Error ? error.message : String(error));
            void this.#closeChannel();

            return;
        }
        for (const message of messages) {
            if (message.kind === 'interleaved') {
                for (const listener of [...this.#interleavedListeners]) {
                    listener(message.channel, message.payload);
                }
            } else if (message.kind === 'response') {
                const cseq = Number(header(message, 'cseq'));
                const request = this.#pending.get(cseq);
                if (!request) {
                    this.#log(`rtsp: answer to unknown request ${cseq}`);
                    continue;
                }
                this.#pending.delete(cseq);
                clearTimeout(request.timer);
                request.resolve(message);
            } else {
                // Requests from the server (for example a keep-alive OPTIONS) are not answered.
                this.#log(`rtsp: ignored a request from the server: ${message.startLine}`);
            }
        }
    }

    #finish(reason: string) : void {
        if (this.#closed) {
            return;
        }

        this.#closed = true;
        clearInterval(this.#keepAliveTimer);
        const error = new RtspError(`RTSP connection closed: ${reason}`);
        for (const request of this.#pending.values()) {
            clearTimeout(request.timer);
            request.reject(error);
        }
        this.#pending.clear();
        for (const listener of [...this.#closeListeners]) {
            listener(reason);
        }
    }

    #send(method: string, url: string, headers: Record<string, string>, timeoutMs: number) : Promise<RtspTextMessage> {
        if (this.#closed) {
            return Promise.reject(new RtspError('RTSP connection closed'));
        }

        const cseq = ++this.#cseq;
        const lines = [`${method} ${url} RTSP/1.0`, `CSeq: ${cseq}`, `User-Agent: ${this.#options.userAgent ?? 'Tilez'}`];
        if (this.#authorization !== undefined) {
            lines.push(`Authorization: ${this.#authorization}`);
        }
        if (this.#session !== undefined) {
            lines.push(`Session: ${this.#session}`);
        }
        for (const [name, value] of Object.entries(headers)) {
            lines.push(`${name}: ${value}`);
        }

        const answer = new Promise<RtspTextMessage>((resolve, reject) => {
            const timer = setTimeout(() => {
                this.#pending.delete(cseq);
                reject(new RtspError(`${method} was not answered within ${timeoutMs} ms`));
            }, timeoutMs);
            this.#pending.set(cseq, { resolve, reject, timer });
        });
        void this.#write(cseq, method, utf8Encode(`${lines.join('\r\n')}\r\n\r\n`));

        return answer;
    }

    /** Sends a request; when that fails, its pending answer fails with the reason. */
    async #write(cseq: number, method: string, bytes: Uint8Array) : Promise<void> {
        try {
            await this.#channel.send(bytes);
        } catch (error) {
            const request = this.#pending.get(cseq);
            if (!request) {
                return;
            }

            this.#pending.delete(cseq);
            clearTimeout(request.timer);
            request.reject(new RtspError(`${method} could not be sent (${String(error)})`, undefined, { cause: error }));
        }
    }

    async #request(
        method: string,
        url: string,
        headers: Record<string, string> = {},
        timeoutMs = this.#options.requestTimeoutMs ?? 10_000,
    ) : Promise<RtspTextMessage> {
        let response = await this.#send(method, url, headers, timeoutMs);
        if (response.status === 401 && this.#authorization === undefined) {
            const challenges = response.headers.get('www-authenticate') ?? [];
            if (this.#credentials && challenges.some((challenge) => /^basic\b/i.test(challenge))) {
                const { username, password } = this.#credentials;
                // btoa takes one character per byte, so the credentials go through UTF-8 first.
                this.#authorization = `Basic ${btoa(String.fromCharCode(...utf8Encode(`${username}:${password}`)))}`;
                response = await this.#send(method, url, headers, timeoutMs);
            } else {
                const schemes = challenges.map((challenge) => challenge.split(/\s/)[0]).join(', ') || 'unknown scheme';
                throw new RtspError(
                    this.#credentials
                        ? `the camera asks for ${schemes} authentication; only Basic is supported so far`
                        : `the camera asks for authentication (${schemes}) and the URL has no credentials`,
                    401,
                );
            }
        }
        if (response.status < 200 || response.status >= 300) {
            throw new RtspError(`${method} failed: ${response.status} ${response.reason}`.trim(), response.status);
        }

        return response;
    }
}
